'use client'
// Navbar: the site header, and SHARED HOT CODE - it loads on every page, so weigh anything added here.
// ⚠ This file accumulated five separate cache-busting artifacts between 2025-11-15 and 2026-01-01
// (two dead date-fns imports, a dead exported NAVBAR_VERSION const, a stale version banner, and two
// "force module graph change" notes describing code that no longer exists). All removed 2026-10-25.
// ⛔ If a stale chunk ever needs busting again, do NOT add a dummy import here: a deploy mints new
// hashes on its own, and whatever is added to this file is paid for on every page of the site.

import React, { useMemo, useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
// 🗑 REMOVED 2026-10-25: three unused date-fns imports assigned to _unused1/2/3, labelled "CRITICAL
// CACHE BUST: force new chunk ID". Added 2025-11-16 (7a439c800) to force a new bundle hash during
// some chunk incident. Every build mints new hashes anyway, so it had no purpose for 11 months - it
// just sat in the Navbar, which is shared hot code on every page.
// ⛔ AND IT WAS NOT COSTING BYTES, so do not record this as a perf win: measured on prod before
// removal with scripts/verify-shipped-string.sh over the 21 chunks the homepage loads, the
// formatDistance en-US locale string "less than a minute" was NOT FOUND, while the positive control
// "Toggle mobile menu" WAS found in the same chunks. formatDistance appears nowhere else in the
// codebase, so webpack was already tree-shaking all three. This is dead-code removal only.
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import { useSession, signOut } from 'next-auth/react'
import CleanProfileFrame from './CleanProfileFrame'
import ThemeToggle from './ThemeToggle'
import IdentityChips from './IdentityChips'
import CrestLevel from './CrestLevel'
import { resolveDisplayTier } from '@/lib/donor-utils'
// Deferred OFF the first-load path (loaded after hydration / on demand). These are interactive
// overlays with no first-paint output: a slide-out watchlist, the support launcher, the price-alert
// toaster (renders only on an alert event), and the Cmd-K command palette. ssr:false keeps them out of
// the server HTML AND out of the layout's initial JS chunk. See defer-navbar-overlays-first-load-1010.
const WatchlistPane = dynamic(() => import('./WatchlistPane'), { ssr: false })

/**
 * 🚨 CRAWLER ATTRS FOR THE TWO SIGN-IN LINKS THAT CARRY A PER-PAGE callbackUrl.
 *
 * Those two links build `/auth?callbackUrl=${encodeURIComponent(pathname)}`, so EVERY page mints a
 * DISTINCT url - /auth?callbackUrl=%2Fstocks%2FNVDA, %2Fstocks%2FNVDA%2Fwhy, and so on across ~32K
 * pages. robots.txt disallows /auth, so Google discovers tens of thousands of urls it is then told
 * not to crawl. Measured 2026-10-17: GSC "Blocked by robots.txt" = 2,907, up from 1,627 on 09-23,
 * growing in step with the site rather than with anything we changed.
 *
 * ⭐ THE BLOCK ITSELF IS CORRECT - /auth should not be indexed. What is wasteful is ADVERTISING
 * 32,000 variants of it. nofollow stops the discovery; noindex covers the case where Google reaches
 * the page from somewhere else ("Indexed, though blocked by robots.txt" is 4 today, so it happens).
 *
 * ⛔ DO NOT REMOVE THE callbackUrl ITSELF. It returns a signed-in user to the page they were on and
 * is a deliberate conversion feature ([[auth-return-to-page-0923]]). These attributes are invisible
 * to users and change nothing about that flow - they only stop crawlers enumerating it.
 * ⚠ The two FIXED links (`callbackUrl=/welcome`) are left alone on purpose: they are one url each,
 * not one per page, so they cost nothing.
 */
const AUTH_LINK_CRAWLER_ATTRS = { rel: 'nofollow' } as const
const SupportLauncher = dynamic(() => import('./SupportFab'), { ssr: false })
import SidebarMenuAvatar from './SidebarMenuAvatar'
import PremiumDaysBadge from './PremiumDaysBadge'
const PriceAlertToaster = dynamic(() => import('./PriceAlertToaster'), { ssr: false })
import MessagesNavButton from './MessagesNavButton'
import PresenceHeartbeat from './PresenceHeartbeat'
import ShowSomeLoveButton from './ShowSomeLoveButton'
import NotificationBell from './NotificationBell'
import HeaderIndices from './HeaderIndices'
import HeaderTopPick from './HeaderTopPick'
import HeaderCalendar from './HeaderCalendar'
import GlobalSearch from './GlobalSearch'
import { useCustomAuth } from '@/hooks/useCustomAuth'
import { useBuildInfo } from './BuildVersion'
import {
  Activity,
  User,
  MessageSquarePlus,
  Heart,
  LifeBuoy,
  Gift,
  Compass,
} from 'lucide-react'
import clsx from 'clsx'
import { sidebarGroupsFor, splitNavByUse, navHref, type NavGroupView } from '@/lib/nav'
import NavMoreSection from '@/components/NavMoreSection'
import { useSidebar } from '@/contexts/SidebarContext'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
// 🗑 REMOVED 2026-10-25: a SECOND dead cache-buster, `import { format } from 'date-fns'` under
// "Force module graph change - import and use a utility". The token `format` appeared exactly once
// in this file - the import itself - so it was never used either. Same story as the block above.
import { prefetchForNavItem } from '@/lib/prefetchPolicy'


// ⭐ Sovereign name flair (owner 2026-08-22). The top of the ledger gets a red-gold gradient name so
// the owner account reads as special, matching the crest ring and the Sovereign chip. Everyone else
// keeps the plain white name with an amber hover — flair is earned, not default.
const SOVEREIGN_NAME = 'bg-gradient-to-r from-rose-200 via-red-300 to-red-500 bg-clip-text text-transparent'



// Status Indicator Component
function StatusIndicator() {
  const [status, setStatus] = useState<'healthy' | 'degraded' | 'down'>('healthy');
  const [openIssues, setOpenIssues] = useState(0);

  useEffect(() => {
    let intervalMs = 30000;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchStatus = async () => {
      try {
        const [healthRes, issuesRes] = await Promise.all([
          fetch('/api/status/health', { signal: AbortSignal.timeout(10000) }),
          fetch('/api/status/issues', { signal: AbortSignal.timeout(10000) }),
        ]);

        if (healthRes.ok) {
          const health = await healthRes.json();
          setStatus(health.overall);
        }

        if (issuesRes.ok) {
          const issues = await issuesRes.json();
          type IssueRow = { status?: string };
          const open =
            (issues.issues as IssueRow[] | undefined)?.filter((i) => i.status !== 'resolved').length ?? 0;
          setOpenIssues(open);
        }
        // On success: reset to normal interval (backoff recovery)
        intervalMs = 30000;
      } catch (error) {
        setStatus('down');
        // Backoff on failure: 30s -> 60s -> 120s max (avoids hammering when server is down)
        intervalMs = Math.min(intervalMs * 2, 120000);
      }
    };

    const scheduleNext = () => {
      timeoutId = setTimeout(async () => {
        await fetchStatus();
        scheduleNext();
      }, intervalMs);
    };

    fetchStatus();
    scheduleNext();
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Link
      href="/status"
      className="relative flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/50 transition-all group"
      title="System Status"
    >
      <div className="relative">
        <Activity className={`h-5 w-5 ${
          status === 'healthy' ? 'text-green-400' :
          status === 'degraded' ? 'text-yellow-400' :
          'text-red-400'
        }`} />
        {/* Status Dot */}
        <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${
          status === 'healthy' ? 'bg-green-400' :
          status === 'degraded' ? 'bg-yellow-400 animate-pulse' :
          'bg-red-400 animate-pulse'
        }`} />
        {/* Issue Count Badge */}
        {openIssues > 0 && (
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold border border-background">
            {openIssues}
          </div>
        )}
      </div>
      <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
        Status
      </span>
    </Link>
  );
}

// NUCLEAR logout function - complete session destruction
const forceSignOut = async () => {
  try {
    console.log('[OUT] Starting NUCLEAR logout process...');
    
    // Step 0: Call NextAuth signOut FIRST to properly clear the session
    try {
      await signOut({ redirect: false }); // Don't redirect yet
      console.log('[OUT] NextAuth signOut completed');
    } catch (authError) {
      console.log('[WARN] NextAuth signOut failed, continuing...');
    }
    
    // Step 1: Call server-side logout API (clears HttpOnly cookies)
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      console.log('[OUT] Server-side logout completed:', response.status);
    } catch (apiError) {
      console.log('[WARN] Server logout failed, continuing...');
    }
    
    // Step 2: Set ULTIMATE NUCLEAR logout flags in ALL storage mechanisms
    if (typeof window !== 'undefined') {
      const logoutTimestamp = Date.now();
      
      // Set ALL possible logout flags in localStorage
      localStorage.setItem('user_logged_out', 'true');
      localStorage.setItem('logout_timestamp', logoutTimestamp.toString());
      localStorage.setItem('nuclear_logout', 'true');
      localStorage.setItem('session_killed', 'true');
      localStorage.setItem('force_logout', 'true');
      
      // Set flags in sessionStorage (survives page refresh)
      sessionStorage.setItem('session_logout', 'true');
      sessionStorage.setItem('session_killed_flag', 'true');
      
      // Set flags in cookies (survives everything)
      document.cookie = 'user_logged_out=true; path=/; max-age=300'; // 5 minutes
      document.cookie = 'session_killed=true; path=/; max-age=300'; // 5 minutes
      
      console.log('[OUT] ULTIMATE NUCLEAR logout flags set in ALL storage mechanisms');
      
      // Clear ALL cookies aggressively
      const allCookies = document.cookie.split(';');
      allCookies.forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        // Clear with multiple domain variations
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.thedesperatetrader.com;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=thedesperatetrader.com;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost;`;
      });
      
      console.log('[OUT] ALL browser data NUCLEAR CLEARED');
    }
    
    // Step 3: Force complete page reload with cache busting
    console.log('[OUT] NUCLEAR redirect to auth page...');
    if (typeof window !== 'undefined') {
      // Force a complete page reload with cache busting
      window.location.replace('/auth?logout=true&nuclear=1&t=' + Date.now() + '&cache=bust');
    }
    
  } catch (error) {
    console.error('[FAIL] NUCLEAR logout error:', error);
    // Ultimate fallback - force complete page reload
    if (typeof window !== 'undefined') {
      window.location.replace('/auth?logout=true&error=1&t=' + Date.now());
    }
  }
};

function NavbarComponent() {
  const { data: session } = useSession()
  // Server-resolved (FeatureFlagsProvider); memberReferral is the owner switch on /admin/integrations.
  const featureFlags = useFeatureFlags()
  const { isAuthenticated, user } = useCustomAuth()
  const router = useRouter()
  const pathname = usePathname()
  // The expanded sidebar IS the WatchlistPane site-wide (owner sign-off 2026-07-07);
  // the collapsed state keeps the classic icon rail below.
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const sidebar = useSidebar()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isLogoutState, setIsLogoutState] = useState(false)
  const buildInfo = useBuildInfo()
  // Always init false to avoid server/client hydration mismatch (window.innerWidth differs)
  const [isDesktop, setIsDesktop] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)
  const dropdownButtonMobileRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null)

  const closeDropdown = () => {
    setDropdownOpen(false)
    setDropdownPosition(null)
  }
  const toggleDropdown = () => setDropdownOpen(prev => !prev)

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (dropdownOpen) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        const buttonRef = isDesktop ? dropdownButtonRef : dropdownButtonMobileRef
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect()
          setDropdownPosition({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right
          })
        }
      })
    } else {
      setDropdownPosition(null)
    }
  }, [dropdownOpen, isDesktop])
  const toggleMobileMenu = () => {
    if (isDesktop) {
      sidebar.toggle()
    } else {
      setMobileMenuOpen(prev => !prev)
    }
  }
  const closeMobileMenu = () => setMobileMenuOpen(false)

  /**
   * ⭐ THE GRANTED TIER IS DATA NOW, NOT A STRING LITERAL (owner 2026-08-16: "set the tier_override
   * and switch the check to read it").
   *
   * This was `username === 'TheDesperateTrader' || name === 'TheDesperateTrader'`, which forced
   * tier10 for one hardcoded account. That comparison was fragile in three ways: a rename silently
   * removed the frame, the same literal was duplicated in UserAvatar.tsx, and no other surface could
   * grant a tier to anyone else without another copy of it.
   *
   * `users.tier_override` has existed in the schema all along and was NULL for all 1,000 accounts.
   * It is now set to 'tier10' on the owner's row and threaded through the session in lib/auth.ts.
   *
   * ⚠ PRECEDENCE IS override > earned > none, and the ORDER IS THE POINT. An override is a manual
   * grant; it must beat the money-derived tier, or granting one to a donor would silently downgrade
   * them. Falling through to `donorTier` means clearing the override restores whatever they actually
   * paid for, which is the correct behaviour for a data-driven rank.
   *
   * ⛔ isCreator IS GONE, NOT RENAMED. Reintroducing a username comparison anywhere in the tier path
   * puts the fragility straight back; navbarTierOverride.test.ts fails if the literal returns.
   */
  const effectiveDonorTier = useMemo(
    () => resolveDisplayTier(user?.donorTier, user?.tierOverride),
    [user?.donorTier, user?.tierOverride],
  )
  const isSovereign = effectiveDonorTier === 'tier10'

  const handleNavigation = (e: React.MouseEvent, href: string) => {
    e.preventDefault()
    closeDropdown()
    router.push(href)
  }

  // Check for logout state - must re-run when isAuthenticated changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const isLogoutPage = window.location.pathname === '/auth' && urlParams.get('logout') === 'true';
    
    if (isLogoutPage) {
      setIsLogoutState(true);
      console.log('[OUT] Navbar detected logout state, forcing logged-out UI');
    } else {
      // Clear logout state when authenticated or not on logout page
      setIsLogoutState(false);
    }
  }, [isAuthenticated]); // Re-run when authentication state changes

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const header = document.querySelector('header')
    const handleScroll = () => {
      if (window.scrollY > 10) {
        header?.classList.add('shadow-lg')
      } else {
        header?.classList.remove('shadow-lg')
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (!dropdownOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if click is inside dropdown using ref
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return; // Click is inside dropdown, don't close
      }

      // Check if click is on the button that opens the dropdown
      const buttonRef = isDesktop ? dropdownButtonRef : dropdownButtonMobileRef
      if (buttonRef.current && buttonRef.current.contains(target)) {
        return; // Click is on the button, don't close (toggleDropdown handles it)
      }

      // Click is outside, close dropdown
      setDropdownOpen(false);
    }

    // Use a small delay to avoid closing immediately when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, isDesktop])


  // Track screen size for sidebar rendering
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 768)
    }
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [mobileMenuOpen])

  // Close mobile menu when clicking on a link
  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false)
  }

  return (
    <>
    {/* ⚠ NOT top-0: the alert banner is fixed at top-0 with z-[9999] and would cover this entire row,
        every control in it included. See ConditionalMain for the measurement. --alert-banner-h is 0px
        whenever no banner is showing, so this is top-0 in every other case. */}
    <header
      style={{ top: 'var(--alert-banner-h, 0px)' }}
      className="fixed left-0 right-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border h-16 shadow-lg shadow-black/20 transition-shadow duration-300 ease-in-out"
    >
      <div className="relative w-full h-16 px-4 md:px-6">
        {/* Single hamburger - ONLY toggle. Sidebar has NO hamburger. */}
        <button
          onClick={toggleMobileMenu}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex md:left-6 items-center justify-center w-10 h-10 rounded-lg text-foreground hover:bg-accent/50 transition-colors"
          aria-label={isDesktop ? (sidebar.expanded ? 'Collapse menu' : 'Expand menu') : 'Toggle mobile menu'}
        >
          <svg
            className={`w-6 h-6 transition-transform duration-200 ${(isDesktop ? sidebar.expanded : mobileMenuOpen) ? 'rotate-90' : 'rotate-0'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {(isDesktop ? sidebar.expanded : mobileMenuOpen) ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Desktop Layout - Show on md+ screens */}
        <div className="hidden md:flex items-center justify-between h-full relative">
          {/* Left: Spacer for hamburger + Logo */}
          <div className="flex items-center gap-3 flex-shrink-0 min-w-0 pl-10">
            {/* Logo - Always visible */}
            <Link
              href={user ? `/profile/${user.username}` : "/"}
              className="logo-link flex items-center gap-2 min-h-8 leading-none"
            >
              <div className="w-8 h-8 opacity-0 animate-fade-in flex items-center justify-center bg-transparent flex-shrink-0">
                <Image
                  src="/assets/logo/thedesperatetrader-logo.svg"
                  alt="Logo"
                  width={32}
                  height={32}
                  className="object-contain"
                  onError={(e) => {
                    console.error('Navbar logo failed to load:', e);
                    e.currentTarget.style.display = 'none';
                  }}
                  priority
                />
              </div>
              <span className="text-lg font-semibold tracking-tight text-foreground opacity-0 animate-fade-in whitespace-nowrap">
                TheDesperate<span className="brand-ink text-amber-600 dark:text-amber-400">Trader</span>
              </span>
            </Link>
            {/* Overall-market pulse — $SPY (S&P 500) price + intraday sparkline, green/red at a glance. */}
            <div className="hidden lg:block"><HeaderIndices compact={!isAuthenticated} /></div>
            {/* The one site-wide Top Pick, same confluence-ranked play as HeroPick — revealed at xl so it
                never crowds the search box on smaller laptops (the mobile drawer carries it below). */}
            {isAuthenticated && <div className="hidden xl:block"><HeaderTopPick /></div>}
          </div>

          {/* Center: always-visible global search (live results, no modal) */}
          <div className={`mx-6 min-w-0 flex-1 ${isAuthenticated ? 'max-w-md' : 'max-w-xl'}`}>
            <GlobalSearch />
          </div>

          {/* Right Side: Status + Profile - Hide Status before overlap with Donate */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Status Indicator (Admin Only) - Hide before 2xl breakpoint to prevent overlap with Donate */}
            {isAuthenticated && user?.username === 'TheDesperateTrader' && (
              <div className="hidden 2xl:block">
                <StatusIndicator />
              </div>
            )}

            {/* ── UTILITY CLUSTER + divider: MEMBERS ONLY (nav-middleground). A logged-out visitor gets a
                   decluttered, conversion-first header: the donate tip, calendar, notifications, DMs,
                   support and price-alert toaster all belong to a signed-in session, so the whole group
                   is gated on auth and does not compete with the primary Start-free CTA. Donate stays
                   reachable from the promo bar; the calendar/support pages stay in the menu. ── */}
            {isAuthenticated && (
              <>
                {/* Tip + note. For members; collapses to the heart alone below lg. */}
                <ShowSomeLoveButton />

                {/* Presence heartbeat (renders nothing), keeps the online-users list live in prod */}
                <PresenceHeartbeat />

                {/* Market calendar, icon + today's-events hover popover, links to /calendar */}
                <div className="hidden sm:block"><HeaderCalendar /></div>

                {/* Notifications, bell + dropdown */}
                <NotificationBell />

                {/* Direct messages, icon + unread badge */}
                <MessagesNavButton />

                {/* Support launcher, questions / issues / requests + reply inbox */}
                <SupportLauncher />

                {/* Fired personal price alerts: OS notification + toast, site-wide */}
                <PriceAlertToaster />

                {/* Divider: separates the utility icons from the account zone so the header reads as
                    ordered groups (brand · search · utilities | account). */}
                <span aria-hidden className="hidden lg:block h-6 w-px bg-border" />
              </>
            )}

            {/* Light/dark theme toggle (owner: white/black toggle, top-right). Shows for everyone. */}
            <ThemeToggle />

            {/* Profile Dropdown */}
            <div className="relative">
              {isAuthenticated && !isLogoutState && user ? (
                <>
                  <button
                    ref={dropdownButtonRef}
                    onClick={(e) => { e.stopPropagation(); toggleDropdown(); }}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-accent/50 transition-all duration-200 group"
                  >
                    {/* Profile Picture with Level Badge */}
                    <div className="relative">
                      <CleanProfileFrame
                        tier={effectiveDonorTier}
                        src={user.image || '/assets/logo/thedesperatetrader-logo.svg'}
                        alt={user.name || 'Profile'}
                        width={36}
                        height={36}
                        animated
                        className="shadow-lg group-hover:scale-105 transition-transform duration-300"
                      />
                      {/**
                        * 🚨 THIS IS WHERE THE STRAY "0" BESIDE THE AVATAR CAME FROM, and it is the
                        * classic React && pitfall rather than a styling problem.
                        *
                        *   {user.level && user.level > 0 && (<badge/>)}
                        *
                        * With `user.level === 0`, the FIRST operand short-circuits and the whole
                        * expression evaluates to the NUMBER 0 - and React renders 0 as text. It does
                        * not render `false`, `null` or `undefined`, but it does render 0. So every
                        * level-0 account got a bare digit floating next to its avatar, which is 847
                        * of 1,000 accounts including the owner's.
                        *
                        * ⭐ THE GUARD MUST PRODUCE A BOOLEAN, not a truthy number. `(user.level ?? 0) > 0`
                        * cannot leak a value into the output no matter what level holds.
                        */}
                      {(user.level ?? 0) > 0 && (
                        <div className="absolute -bottom-1 -right-1 grid place-items-center min-w-[20px] h-5 px-1 rounded-full bg-card dark:bg-gradient-to-b dark:from-zinc-800 dark:to-zinc-950 text-amber-300 font-bold text-[11px] leading-none border border-amber-400/80 shadow-[0_1px_5px_rgba(0,0,0,0.75)]">
                          {user.level}
                        </div>
                      )}
                    </div>

                  {/* User Info */}
                  <div className="flex flex-col items-start min-w-0">
                    <span className={clsx(
                      'font-semibold truncate max-w-[180px] transition-colors',
                      isSovereign ? `${SOVEREIGN_NAME} font-bold` : 'text-foreground group-hover:text-amber-400',
                    )}>
                      {user.username || user.name}
                    </span>
                    {/**
                      * 🚨 THIS ROW SHOWED A ROLE WHERE THE OWNER EXPECTED A RANK (owner 2026-08-16:
                      * "not using banner based on money spent ... this is supposed to show level as
                      * well"). `user.tier` is the ROLE column - measured in prod it holds 'Lurker'
                      * for 997 accounts, 'admin' for 2 and 'free' for 1 - so the chip read "admin"
                      * beside "Unlimited" and said nothing about standing.
                      *
                      * The rank that IS earned by money spent is `donorTier`, and it works: all 18
                      * accounts with total_donated > 0 carry the right tier. It simply was not shown
                      * here. Now the chip is the tier NAME (Neophyte ... Sovereign) in the tier's own
                      * colour, so the chip and the avatar ring say the same thing.
                      *
                      * ⚠ ADMIN IS NOT DROPPED, it is demoted to what it is: a role marker, kept
                      * because the owner uses it to confirm which account is signed in.
                      */}
                    <div className="flex items-center gap-1">
                      {/* ⭐ COMPACT: RANK ONLY. Owner 2026-08-20, "so many weird badges" - this
                          button carried four chips (Sovereign / New / admin / ∞ Unlimited) in
                          ~200px. admin and the level moved to the dropdown below, which renders the
                          SAME component in 'full'. The level chip is also gone for everyone who has
                          no level, because nothing awards XP: max(exp) = 0 across all 1,037
                          accounts. */}
                      <IdentityChips
                        variant="compact"
                        donorTier={effectiveDonorTier}
                        level={user.level}
                        isAdmin={user.tier === 'admin' || user.isAdmin === true}
                      />
                      <PremiumDaysBadge />
                    </div>
                  </div>

                  {/* Dropdown Arrow */}
                  <svg
                    className={`w-4 h-4 text-muted-foreground transition-transform duration-200 group-hover:text-amber-400 ${dropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Enhanced Dropdown Menu - Rendered via portal below */}
              </>
            ) : (
              <div className="flex items-center gap-2.5">
                {/* Middle-ground logged-out CTA (nav-middleground): Sign in is a quiet returning-user
                    link; Start free is the dominant conversion CTA with a reassurance line, since a
                    stranger from search weighs "do I trust this" more than "do I have an account".
                    Register: visitor-signup-cta-0822. A matched secondary+primary pair (nav-auth-polish-1020):
                    both h-10 and rounded-lg so they read as one unit, not a bare link beside a block. */}
                <Link
                  href={pathname && !pathname.startsWith('/auth') ? `/auth?callbackUrl=${encodeURIComponent(pathname)}` : '/auth'}
                  prefetch={false}
                  {...AUTH_LINK_CRAWLER_ATTRS}
                  className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg px-4 text-sm font-semibold text-foreground ring-1 ring-inset ring-border dark:ring-white/10 transition-colors hover:bg-accent dark:hover:bg-white/[0.06] hover:text-foreground hover:ring-border dark:hover:ring-white/20"
                >
                  Sign in
                </Link>
                <Link
                  href="/auth?mode=signup&callbackUrl=/welcome"
                  prefetch={false}
                  className="inline-flex h-10 flex-col items-center justify-center whitespace-nowrap rounded-lg bg-amber-500 px-5 leading-none text-black shadow-[0_1px_10px_rgba(245,158,11,0.30)] transition-colors hover:bg-amber-400"
                >
                  <span className="text-[13px] font-bold tracking-[0.01em]">Start free</span>
                  <span className="mt-[2px] text-[10px] font-medium text-amber-950/80">5 days free · no card</span>
                </Link>
              </div>
            )}
            </div>
          </div>
        </div>


        {/* Mobile Layout */}
        <div className="md:hidden flex items-center gap-2 h-full">
          {/* Left: Spacer for shared hamburger + logo icon (text hidden to make room for search) */}
          <div className="flex-shrink-0 w-8" aria-hidden />
          <Link
            href={user ? `/profile/${user.username}` : "/"}
            className="logo-link flex items-center min-h-8 leading-none flex-shrink-0"
          >
            <div className="w-8 h-8 opacity-0 animate-fade-in flex items-center justify-center bg-transparent flex-shrink-0">
              <Image
                src="/assets/logo/thedesperatetrader-logo.svg"
                alt="Logo"
                width={32}
                height={32}
                className="object-contain"
                onError={(e) => {
                  console.error('Mobile navbar logo failed to load:', e);
                  e.currentTarget.style.display = 'none';
                }}
                priority
              />
            </div>
          </Link>

          {/* Center: always-visible global search. Market indices, donate and support moved into the
              hamburger drawer 2026-08-23 — the phone bar was cramming 8 elements into 64px and the chip
              collided with the icons. A mobile top bar carries at most menu, brand, search and the two
              things people reach for (notifications + account); the tape now lives in the drawer's
              Markets ribbon, so the search gets a real, one-handed-tappable field. */}
          <div className="min-w-0 flex-1">
            <GlobalSearch />
          </div>

          {/* Right: Notifications + Profile only. Donate and support moved to the drawer so the bar can
              breathe; these two are the actions people actually reach for on a phone. */}
          <div className="relative flex-shrink-0 flex items-center gap-1.5">
            <ThemeToggle className="h-8 w-8" />
            {isAuthenticated && !isLogoutState && <NotificationBell />}
            {isAuthenticated && !isLogoutState && user ? (
              <>
                <button
                  ref={dropdownButtonMobileRef}
                  onClick={(e) => { e.stopPropagation(); toggleDropdown(); }}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-foreground hover:bg-accent/50 transition-all duration-200 group"
                >
                  <div className="relative">
                    <CleanProfileFrame
                      tier={effectiveDonorTier}
                      src={user.image || '/assets/logo/thedesperatetrader-logo.svg'}
                      alt={user.name || 'Profile'}
                      width={32}
                      height={32}
                      animated
                      className="shadow-lg group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Same React && pitfall as the desktop badge above: `0 && ...` evaluates to 0
                        and React renders it. Boolean guard, not a truthy number. */}
                    {(user.level ?? 0) > 0 && (
                      <div className="absolute -bottom-1 -right-1 grid place-items-center min-w-[16px] h-4 px-0.5 rounded-full bg-card dark:bg-gradient-to-b dark:from-zinc-800 dark:to-zinc-950 text-amber-300 font-bold text-[10px] leading-none border border-amber-400/80 shadow-[0_1px_4px_rgba(0,0,0,0.7)]">
                        {user.level}
                      </div>
                    )}
                  </div>

                  <svg
                    className={`w-4 h-4 text-muted-foreground transition-transform duration-200 group-hover:text-amber-400 ${dropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Mobile Dropdown - Rendered via portal below */}
              </>
            ) : (
              /* Signed out, phone bar: ONE primary CTA only. Two text buttons here
                 (Start Free + Sign In) crowded the flex-1 search down to 35x20px at
                 375 and to zero at 320 - the search collapse fixed 2026-08-23
                 (mobile-search-collapses-signed-out-0823). Search is the hero action on
                 a ticker site, so it keeps the width; Sign In (a returning-user link, not
                 a conversion driver) lives in the hamburger drawer bottom. Desktop keeps
                 both. */
              <Link
                href="/auth?mode=signup&callbackUrl=/welcome"
                prefetch={false}
                className="flex-shrink-0 text-sm px-3 py-2 rounded font-semibold transition bg-amber-500 text-black hover:bg-amber-400"
              >
                Start Free
              </Link>
            )}
          </div>
        </div>

        {/* Desktop: Persistent left sidebar (YouTube-style). Portal to body so it appears above content. */}
        {isDesktop && createPortal(
          <div
            // top-16 was "directly under the header". The header itself now sits below the alert
            // banner, so this has to follow it or a banner-height gap opens above the sidebar.
            className="hidden md:flex fixed left-0 bottom-0 z-[9998] flex-col overflow-y-auto overflow-x-hidden border-r border-border bg-card dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900/98 dark:to-zinc-950 transition-[width] duration-200 ease-out"
            style={{ top: 'calc(4rem + var(--alert-banner-h, 0px))', width: sidebar.width }}
          >
            {/* Subtle gradient accent on left edge when expanded */}
            {sidebar.expanded && (
              <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-0 w-px bg-gradient-to-b from-amber-500/30 via-amber-500/10 to-transparent" />
            )}
            <div
              className="pointer-events-none absolute inset-0 z-0 opacity-[0.03] hidden dark:block"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.2) 1px, transparent 0)',
                backgroundSize: '24px 24px',
              }}
            />
            {sidebar.expanded ? (
              <div className="relative z-[1] flex flex-1 min-h-0 flex-col">
                <WatchlistPane />
              </div>
            ) : (
            <div
              className={clsx(
                'relative z-[1] flex flex-1 min-h-0 flex-col',
                sidebar.expanded ? 'px-3' : 'px-2'
              )}
            >
              {/* Header: Menu label when expanded */}
              {sidebar.expanded && (
                <div className="flex items-center gap-2.5 flex-shrink-0 pt-4 pb-3 min-w-0">
                  <div className="w-1 h-5 bg-gradient-to-b from-amber-400 to-amber-600 rounded-full flex-shrink-0 shadow-sm shadow-amber-500/20" />
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest truncate">Menu</span>
                </div>
              )}
              {/* User block when expanded */}
              {user && sidebar.expanded && (
                <Link
                  href={`/profile/${user.username}`}
                  className={clsx(
                    'flex items-center gap-3 py-3 px-3 rounded-xl mx-0.5 mb-2 transition-all duration-200',
                    pathname?.startsWith('/profile') ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-muted/30 border border-transparent hover:bg-accent/50 hover:border-border/50'
                  )}
                >
                  <SidebarMenuAvatar
                    donorTier={effectiveDonorTier}
                    src={user.image || '/assets/logo/thedesperatetrader-logo.svg'}
                    alt={user.name || 'Profile'}
                    size={36}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{user.name || user.username}</div>
                    <div className="text-xs text-muted-foreground truncate">@{user.username}</div>
                    <div className="mt-1"><PremiumDaysBadge /></div>
                  </div>
                </Link>
              )}
              {user && !sidebar.expanded && (
                <Link
                  href={`/profile/${user.username}`}
                  className="flex justify-center rounded-full py-3 outline-none transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-amber-500/40"
                  title={`@${user.username}`}
                >
                  <SidebarMenuAvatar
                    donorTier={effectiveDonorTier}
                    src={user.image || '/assets/logo/thedesperatetrader-logo.svg'}
                    alt={user.name || 'Profile'}
                    size={32}
                  />
                </Link>
              )}
              {/* Nav links - active state and hover polish */}
              <nav className="flex flex-col py-2 gap-1 flex-1 min-h-0">
                {(() => {
                  const isActive = (href: string, profileMatch?: boolean) =>
                    profileMatch ? pathname?.startsWith('/profile') : (pathname === href || (href !== '/' && pathname?.startsWith(href + '/')))
                  const linkClass = (href: string, profileMatch?: boolean) =>
                    clsx(
                      'flex items-center rounded-xl transition-all duration-200 min-h-[44px]',
                      sidebar.expanded ? 'gap-3 px-3 py-2.5' : 'justify-center py-3',
                      isActive(href, profileMatch)
                        ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25 shadow-sm'
                        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground hover:border-border/50 border border-transparent'
                    )
                  return (
                    <>
                      {(() => {
                        // ✂ Low-use links fold into "More pages" (full-nav-trim-1030); same section markup.
                        const { main, more } = splitNavByUse(sidebarGroupsFor({ isAdmin: isAuthenticated && user?.username === 'TheDesperateTrader' }))
                        const renderSection = (section: NavGroupView, si: number) => (
                        <div key={section.id} className="contents">
                          {/* A heading when there is room for words, a hairline when there is not. The
                              collapsed rail is icons only, so an unlabelled divider is the only grouping
                              cue that fits. */}
                          {sidebar.expanded ? (
                            <div className={clsx('px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground', si === 0 ? 'pt-1' : 'pt-3')}>
                              {section.label}
                            </div>
                          ) : (
                            si > 0 && <div className="h-px bg-muted/60 my-2 mx-2" aria-hidden />
                          )}
                          {section.items.map((item) => {
                            const Icon = item.Icon
                            const href = navHref(item, { signedIn: !!user, username: user?.username })
                            return (
                              <Link
                                key={item.href}
                                href={href}
                                prefetch={prefetchForNavItem(item.href)}
                                className={clsx(linkClass(item.href, item.href === '/profile'), section.id === 'core' && 'font-semibold')}
                                title={!sidebar.expanded ? item.label : item.desc}
                              >
                                <Icon className="w-5 h-5 flex-shrink-0" />
                                {sidebar.expanded && <span className="truncate text-sm font-medium">{item.label}</span>}
                              </Link>
                            )
                          })}
                        </div>
                        )
                        const moreItems = more.flatMap((g) => g.items)
                        return (
                          <>
                            {main.map(renderSection)}
                            {moreItems.length > 0 && (
                              <NavMoreSection compact={!sidebar.expanded} count={moreItems.length} hrefs={moreItems.map((i) => i.href)} className="mt-2" rowClassName="min-h-[44px] gap-3 rounded-xl px-3 py-2.5 text-sm font-medium">
                                {more.map((g, i) => renderSection(g, i + 1))}
                              </NavMoreSection>
                            )}
                          </>
                        )
                      })()}
                      {/* Guide launcher, always present so guidance is opt-in from any page (owner 2026-09-30:
                          "put guide buttons on each page ... but not force it"). Links to the on-demand Site Guide;
                          the post-signup checklist stays on /welcome, no longer forced. */}
                      <Link
                        href="/site-guide"
                        prefetch={false}
                        title={!sidebar.expanded ? 'Guide' : 'How every page works, and where to start'}
                        className={clsx(
                          'flex w-full items-center rounded-xl transition-all duration-200 min-h-[44px] border border-transparent mt-2',
                          sidebar.expanded ? 'gap-3 px-3 py-2.5' : 'justify-center py-3',
                          'text-sky-300/90 hover:bg-sky-500/10 hover:text-sky-200 hover:border-sky-500/25'
                        )}
                      >
                        <Compass className="w-5 h-5 flex-shrink-0" />
                        {sidebar.expanded && <span className="truncate text-sm font-medium">Guide</span>}
                      </Link>
                      {/* Feedback opens the centered feedback modal (FeedbackWidget listens for this event).
                          NOT in the nav model: it is an action, not a page, so it has no href to document. */}
                      <button
                        type="button"
                        onClick={() => { try { window.dispatchEvent(new CustomEvent('dt:open-feedback')) } catch { /* noop */ } }}
                        title={!sidebar.expanded ? 'Send feedback' : undefined}
                        className={clsx(
                          'flex w-full items-center rounded-xl transition-all duration-200 min-h-[44px] border border-transparent mt-2',
                          sidebar.expanded ? 'gap-3 px-3 py-2.5' : 'justify-center py-3',
                          'text-amber-300/90 hover:bg-amber-500/10 hover:text-amber-200 hover:border-amber-500/25'
                        )}
                      >
                        <MessageSquarePlus className="w-5 h-5 flex-shrink-0" />
                        {sidebar.expanded && <span className="truncate text-sm font-medium">Feedback</span>}
                      </button>
                    </>
                  )
                })()}
              </nav>
            </div>
            )}
          </div>,
          document.body
        )}

        {/* Mobile Menu - portal to body; only when !isDesktop so we never stack with desktop sidebar */}
        {mobileMenuOpen && !isDesktop && createPortal(
          <div className="fixed inset-0 z-[9999] flex md:hidden">
            <div
              ref={mobileMenuRef}
              className="w-[min(280px,85vw)] max-w-full h-full bg-card dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900/98 dark:to-zinc-950 border-r border-border/90 flex flex-col overflow-hidden shrink-0 shadow-[4px_0_24px_-4px_rgba(0,0,0,0.5)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-amber-500/20 via-transparent to-transparent pointer-events-none" />
              <div className="absolute inset-0 opacity-[0.03] pointer-events-none hidden dark:block" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.2) 1px, transparent 0)', backgroundSize: '24px 24px' }} />
              {/* Single unified scrollable menu - no stacked sections */}
              <nav className="relative flex flex-col py-3 overflow-y-auto flex-1 min-h-0 scrollbar-thin scrollbar-track-zinc-800 scrollbar-thumb-zinc-600">
                {/* Profile card at top */}
                {isAuthenticated && user ? (
                  <Link
                    href={`/profile/${user.username}`}
                    onClick={handleMobileLinkClick}
                    className={clsx(
                      'flex items-center gap-3 px-4 py-3 mx-3 mt-2 mb-2 rounded-xl border transition-all duration-200',
                      pathname?.startsWith('/profile') ? 'bg-amber-500/10 border-amber-500/20' : 'bg-muted/30 border-transparent hover:bg-accent/50 hover:border-border/50'
                    )}
                  >
                    <CleanProfileFrame
                      tier={effectiveDonorTier}
                      src={user.image || '/assets/logo/thedesperatetrader-logo.svg'}
                      alt={user.name || 'Profile'}
                      width={40}
                      height={40}
                      animated
                      className="flex-shrink-0 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-foreground truncate">{user.username || user.name}</div>
                      <div className="text-xs text-muted-foreground truncate">@{user.username}</div>
                    </div>
                  </Link>
                ) : null}

                {/* Markets — the live tape, moved off the top bar so it can breathe. Scrolls
                    horizontally; each index taps through to its chart. */}
                <div className="mx-3 mb-2 rounded-xl border border-border/70 bg-card/40 px-3.5 py-2.5">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Markets</div>
                  <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    <HeaderTopPick variant="ribbon" />
                    <HeaderIndices variant="ribbon" />
                  </div>
                </div>

                {/* Nav links - active state and polish */}
                <div className="px-3 space-y-0.5">
                  {(() => {
                    const mobileActive = (href: string, profileMatch?: boolean) =>
                      profileMatch ? pathname?.startsWith('/profile') : (pathname === href || (href !== '/' && pathname?.startsWith(href + '/')))
                    const mobileLinkClass = (href: string, profileMatch?: boolean) =>
                      clsx(
                        'mobile-nav-link min-h-[44px] flex items-center px-4 rounded-xl transition-all duration-200',
                        mobileActive(href, profileMatch) && '!bg-amber-500/15 !text-amber-400 border border-amber-500/25'
                      )
                    return (
                      <>
                        {(() => {
                          // ✂ Low-use links fold into "More pages" (full-nav-trim-1030); same section markup.
                          const { main, more } = splitNavByUse(sidebarGroupsFor({ isAdmin: isAuthenticated && (user?.username === 'TheDesperateTrader' || user?.name === 'TheDesperateTrader') }))
                          const renderSection = (section: NavGroupView, si: number) => (
                          <div key={section.id}>
                            <div className={clsx('px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground', si === 0 ? 'pt-1' : 'pt-3')}>
                              {section.label}
                            </div>
                            {section.items.map((item) => {
                              const Icon = item.Icon
                              const href = navHref(item, { signedIn: !!user, username: user?.username })
                              return (
                                <Link
                                  key={item.href}
                                  href={href}
                                  prefetch={prefetchForNavItem(item.href)}
                                  className={clsx(mobileLinkClass(item.href, item.href === '/profile'), 'gap-3')}
                                  onClick={handleMobileLinkClick}
                                >
                                  {/* Icons on mobile too. The drawer previously rendered bare text, so the
                                      two menus did not even look like the same navigation. */}
                                  <Icon className="w-4 h-4 flex-shrink-0 opacity-70" />
                                  <span className="truncate">{item.label}</span>
                                </Link>
                              )
                            })}
                          </div>
                          )
                          const moreItems = more.flatMap((g) => g.items)
                          return (
                            <>
                              {main.map(renderSection)}
                              {moreItems.length > 0 && (
                                <NavMoreSection count={moreItems.length} hrefs={moreItems.map((i) => i.href)} className="mt-3" rowClassName="min-h-[44px] gap-3 rounded-xl px-4 text-sm">
                                  {more.map((g, i) => renderSection(g, i + 1))}
                                </NavMoreSection>
                              )}
                            </>
                          )
                        })()}
                      </>
                    )
                  })()}
                </div>

                {/* Support + Donate — relocated from the top bar; available to everyone. */}
                <div className="px-3 mt-2 space-y-0.5">
                  {/* Refer and earn (affiliate) — the mobile counterpart of the account-menu item. */}
                  <Link
                    href="/affiliate"
                    onClick={handleMobileLinkClick}
                    className="mobile-nav-link min-h-[44px] flex items-center gap-3 px-4 rounded-xl"
                  >
                    <Gift className="w-4 h-4 flex-shrink-0 opacity-70" />
                    <span className="truncate">Refer and earn</span>
                  </Link>
                  <Link
                    href="/support"
                    onClick={handleMobileLinkClick}
                    className="mobile-nav-link min-h-[44px] flex items-center gap-3 px-4 rounded-xl"
                  >
                    <LifeBuoy className="w-4 h-4 flex-shrink-0 opacity-70" />
                    <span className="truncate">Support</span>
                  </Link>
                  <Link prefetch={false}
                    href="/donate"
                    onClick={handleMobileLinkClick}
                    className="mobile-nav-link min-h-[44px] flex items-center gap-3 px-4 rounded-xl text-amber-300"
                  >
                    <Heart className="w-4 h-4 flex-shrink-0 fill-amber-400/25" />
                    <span className="truncate">Show some love</span>
                  </Link>
                </div>

                {/* Sign Out at bottom */}
                {isAuthenticated && user ? (
                  <div className="mt-auto pt-3 px-3 border-t border-border/80">
                    <button
                      onClick={async () => { handleMobileLinkClick(); await forceSignOut(); }}
                      className="mobile-nav-link min-h-[44px] w-full flex items-center px-4 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="mt-auto pt-3 px-3 border-t border-border/80">
                    <Link prefetch={false} {...AUTH_LINK_CRAWLER_ATTRS} href={pathname && !pathname.startsWith('/auth') ? `/auth?callbackUrl=${encodeURIComponent(pathname)}` : '/auth'} onClick={handleMobileLinkClick} className="mobile-nav-link min-h-[44px] flex items-center px-4 justify-center rounded-xl text-amber-400 hover:bg-amber-500/10 transition-colors">
                      Sign In
                    </Link>
                  </div>
                )}
              </nav>
            </div>
            <div className="flex-1 bg-background/40 min-w-0" onClick={closeMobileMenu} aria-hidden />
          </div>,
          document.body
        )}
      </div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-amber-400/0 via-amber-400/50 to-amber-400/0"></div>

      <style jsx global>{`
        @keyframes dropdown-enter {
          0% { opacity: 0; transform: translateY(-8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-dropdown-enter {
          animation: dropdown-enter 200ms ease-out;
        }
               @keyframes slide-down {
                 0% { opacity: 0; transform: translateY(-10px); }
                 100% { opacity: 1; transform: translateY(0); }
               }
               .animate-slide-down {
                 animation: slide-down 200ms ease-out;
               }
               @keyframes slide-in-left {
                 0% { opacity: 0; transform: translateX(-100%); }
                 100% { opacity: 1; transform: translateX(0); }
               }
               .animate-slide-in-left {
                 animation: slide-in-left 250ms ease-out;
               }
        @keyframes fade-in {
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease forwards;
        }
        .nav-link {
          font-size: 0.875rem;
          font-weight: 500;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          transition: all 0.2s ease;
          text-decoration: none;
          flex-shrink: 0;
        }
        .nav-link:hover {
          color: #fbbf24;
          background-color: rgba(39, 39, 42, 0.6);
          text-decoration: underline;
          text-underline-offset: 4px;
        }

        @media (max-width: 475px) {
          .xs\\:block {
            display: none !important;
          }
        }
        .mobile-nav-link {
          font-size: 0.9375rem;
          font-weight: 500;
          color: #d4d4d8;
          padding: 0.875rem 1rem;
          display: flex;
          align-items: center;
          text-decoration: none;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 0.625rem;
          margin: 0.125rem 0;
          gap: 0.875rem;
          position: relative;
          letter-spacing: 0.01em;
        }
        .mobile-nav-link::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 0;
          background: linear-gradient(to bottom, #fbbf24, #f59e0b, #d97706);
          border-radius: 0 3px 3px 0;
          transition: height 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 0 8px rgba(251, 191, 36, 0.3);
        }
        .mobile-nav-link::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 0.625rem;
          padding: 1px;
          background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0));
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .mobile-nav-link svg {
          flex-shrink: 0;
          opacity: 0.9;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          width: 1.25rem;
          height: 1.25rem;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
        }
        .mobile-nav-link:hover {
          color: #ffffff;
          background: linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
          transform: translateX(4px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }
        .mobile-nav-link:hover::before {
          height: 70%;
          box-shadow: 0 0 12px rgba(251, 191, 36, 0.5);
        }
        .mobile-nav-link:hover::after {
          opacity: 1;
        }
        .mobile-nav-link:hover svg {
          opacity: 1;
          transform: scale(1.08) translateX(2px);
          filter: drop-shadow(0 2px 4px rgba(251, 191, 36, 0.3));
        }
        .mobile-nav-link:active {
          background: linear-gradient(90deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%);
          transform: translateX(4px) scale(0.98);
        }
        .mobile-nav-link span {
          flex: 1;
          transition: all 0.25s ease;
          font-weight: 500;
        }
        .mobile-nav-link:hover span {
          font-weight: 600;
        }
        /* Premium link styling */
        .mobile-nav-link-premium {
          background: linear-gradient(90deg, rgba(251, 191, 36, 0.08) 0%, transparent 100%);
        }
        .mobile-nav-link-premium:hover {
          background: linear-gradient(90deg, rgba(251, 191, 36, 0.15) 0%, rgba(251, 191, 36, 0.05) 100%);
        }
        .mobile-nav-link-premium svg {
          color: #fbbf24;
        }
        /* Admin link styling */
        .mobile-nav-link-admin {
          background: linear-gradient(90deg, rgba(139, 92, 246, 0.08) 0%, transparent 100%);
        }
        .mobile-nav-link-admin:hover {
          background: linear-gradient(90deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%);
        }
        .mobile-nav-link-admin svg {
          color: #8b5cf6;
        }
        .mobile-action-link {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: white;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
          text-decoration: none;
        }
        .mobile-action-link:hover {
          background-color: rgba(39, 39, 42, 0.6);
        }

        .logo-link {
          text-decoration: none;
          color: inherit;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          user-select: none;
          min-height: 2rem;
          line-height: 1;
        }
        .logo-link:hover {
          opacity: 0.8;
        }
        /* Custom scrollbar for sidebar */
        .fixed.overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        .fixed.overflow-y-auto::-webkit-scrollbar-track {
          background: transparent;
        }
        .fixed.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }
        .fixed.overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>

      {/* User Dropdown Menu - Render via Portal to appear above everything */}
      {dropdownOpen && dropdownPosition && isAuthenticated && user && createPortal(
        <>
          {/* Backdrop - click to close */}
          <div
            className="fixed inset-0 bg-transparent"
            style={{ 
              zIndex: 99998,
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onClick={closeDropdown}
          />
          {/* Dropdown - positioned based on button location - MUST be highest z-index */}
          <div
            ref={dropdownRef}
            className={`fixed bg-card/95 backdrop-blur-sm border border-border/50 rounded-xl shadow-2xl animate-dropdown-enter overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:theme(colors.zinc.600)_transparent] ${
              isDesktop ? 'w-72' : 'w-64'
            }`}
            style={{
              top: dropdownPosition ? `${dropdownPosition.top}px` : '64px',
              right: dropdownPosition ? `${dropdownPosition.right}px` : '16px',
              // Never run off the bottom of the screen: cap to the space below the button and scroll the rest.
              maxHeight: dropdownPosition ? `calc(100vh - ${dropdownPosition.top + 16}px)` : '80vh',
              zIndex: 2147483647, // Maximum z-index value (2^31 - 1)
              position: 'fixed',
              isolation: 'isolate', // Create new stacking context to escape parent contexts
              pointerEvents: 'auto', // Ensure it can receive pointer events
            }}
            data-dropdown-portal="true"
            data-z-index="2147483647"
            onClick={(e) => e.stopPropagation()}
          >
            {/* User Header in Dropdown — a faint tier-tinted wash makes the card feel like part of the
                crest rather than a plain menu, strongest for Sovereign. */}
            <div
              className={clsx(isDesktop ? 'p-4' : 'p-3', 'relative overflow-hidden border-b border-border/50')}
              style={isSovereign ? {
                background: 'radial-gradient(120% 120% at 85% -10%, rgba(239,68,68,0.14), transparent 60%), linear-gradient(180deg, rgba(251,191,36,0.06), transparent 70%)',
              } : undefined}
            >
              <div className="relative flex items-center gap-3">
                <div className="relative">
                  <CleanProfileFrame
                    tier={effectiveDonorTier}
                    src={user.image || '/assets/logo/thedesperatetrader-logo.svg'}
                    alt={user.name || 'Profile'}
                    width={44}
                    height={44}
                    animated
                    className="shadow-lg"
                  />
                  {(user.username === 'TheDesperateTrader' || user.name === 'TheDesperateTrader') && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full border-2 border-background shadow-lg animate-pulse" title="Creator" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={clsx(
                    'font-semibold truncate',
                    isDesktop ? 'text-base' : 'text-sm',
                    isSovereign ? SOVEREIGN_NAME : 'text-foreground',
                  )}>
                    {user.username || user.name}
                  </h3>
                  <p className={`text-muted-foreground truncate ${isDesktop ? 'text-sm' : 'text-xs'}`}>
                    {user.email}
                  </p>
                  {/**
                    * ⛔ THIS ROW USED TO STATE A LEVEL NOBODY HAS. `Level ${user.level || 1}` prints
                    * "Level 1" for a level-0 account, and 847 of 1,000 accounts are level 0 - so the
                    * dropdown told almost every user they had reached a level they had not, while
                    * the avatar badge beside it (correctly guarded on level > 0) showed nothing.
                    * Two elements, same fact, opposite answers.
                    *
                    * Now it renders the SAME <IdentityChips/> as the collapsed button above, so the
                    * two surfaces cannot drift apart again, and the second chip is the donor tier
                    * earned by money spent rather than the role column.
                    */}
                  <div className={clsx('flex flex-wrap items-center mt-1', isDesktop ? 'gap-2' : 'gap-1')}>
                    {/* Rank + role here; the LEVEL is shown by the richer CrestLevel block below, so
                        showLevel is off to avoid two elements stating the same number. */}
                    <IdentityChips
                      donorTier={effectiveDonorTier}
                      level={user.level}
                      isAdmin={user.tier === 'admin' || user.isAdmin === true}
                      showLevel={false}
                    />
                  </div>
                </div>
              </div>

              {/* The player-card level block: earned title, level number and XP-to-next bar. Renders
                  nothing until the account has XP, so a new member sees a clean crest. */}
              <CrestLevel xp={user.renown} compact className="mt-3" />
            </div>

            {/* Dropdown Options */}
            <div className="py-2">
              <Link
                href={`/profile/${user.username}`}
                onClick={closeDropdown}
                className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
              >
                <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>View Profile</span>
              </Link>

              {/* Subscription & billing — the account menu is where people look to manage or CANCEL, and users
                  kept reporting they could not find it (2026-09-06). Made deliberately prominent (amber card,
                  subtitle, chevron) and placed at the top so it is unmissable. One entry covers everyone:
                  /subscribe shows plans to free users, the premium-days state to holders, and the Manage +
                  Cancel Subscription flow to subscribers. */}
              <Link
                href="/subscribe"
                onClick={closeDropdown}
                className="group mx-2 my-1 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-left transition-colors hover:border-amber-500/50 hover:bg-amber-500/15"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 ring-1 ring-amber-500/25">
                  <svg className="h-4 w-4 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-amber-100 group-hover:text-amber-50">Subscription &amp; billing</span>
                  <span className="block text-[11px] leading-tight text-amber-200/60">Upgrade, manage, or cancel</span>
                </span>
                <svg className="h-4 w-4 shrink-0 text-amber-400/70 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>

              {/* Refer and earn (affiliate discoverability). A calm menu item, NOT a banner: subscribers
                  are promised freedom from nudges and are also the 20% tier, so the entry point reaches
                  the engaged, signed-in cohort here without spending that promise. Pairs with the footer
                  link (the site-wide entry) by adding a higher-visibility spot for logged-in users. */}
              {/* While the member loop is on (owner switch, /admin/integrations), the same calm item points to the
                  one-click invite page instead: the affiliate route asks people to APPLY, and 897 emailed produced
                  0 applications (affiliate_program, settled 10-11). Before 2026-10-20 nothing linked to /invite at
                  all, so the loop could have been switched on and reached nobody. */}
              <Link
                href={featureFlags.memberReferral ? '/invite' : '/affiliate'}
                onClick={closeDropdown}
                className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
              >
                <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
                {featureFlags.memberReferral ? (
                  <span className="min-w-0">
                    <span className="block">Invite friends</span>
                    <span className="block text-[11px] leading-tight text-zinc-500">You both get {featureFlags.memberReferralRewardDays} free days</span>
                  </span>
                ) : (
                  <span>Refer and earn</span>
                )}
              </Link>

              {/* Admin Log Review - Only for TheDesperateTrader */}
              {(user.username === 'TheDesperateTrader' || user.name === 'TheDesperateTrader') && (
                <Link
                  href="/admin/logs"
                  onClick={closeDropdown}
                  className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                >
                  <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Log Review</span>
                </Link>
              )}

              {/* Labs Workspace sandbox - Only for TheDesperateTrader (design preview, not public) */}
              {(user.username === 'TheDesperateTrader' || user.name === 'TheDesperateTrader') && (
                <Link
                  href="/labs/workspace"
                  onClick={closeDropdown}
                  className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                >
                  <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                  <span>Chart Workspace</span>
                  <span className="ml-auto rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">Labs</span>
                </Link>
              )}

              {/* Email Marketing composer, owner only */}
              {(user.username === 'TheDesperateTrader' || user.name === 'TheDesperateTrader') && (
                <Link
                  href="/email-marketing"
                  onClick={closeDropdown}
                  className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                >
                  <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Email Marketing</span>
                </Link>
              )}

              {/* Admin Links - Only for TheDesperateTrader */}
              {(user.username === 'TheDesperateTrader' || user.name === 'TheDesperateTrader') && (
                <>
                  <Link
                    href="/admin/articles"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m0 0v11a2 2 0 002-2V8a2 2 0 00-2-2h-1m-6 4h4m-4 4h4m-8-8h.01M8 12h.01M8 16h.01" />
                    </svg>
                    <span>Articles &amp; News</span>
                  </Link>
                  <Link
                    href="/admin/trade-idea"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span>Trade Idea Draft</span>
                  </Link>
                  <Link
                    href="/admin/affiliates"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112-2v2m-2 0h2m-2 0H8m4 0a2 2 0 10-2-2v2m0 0H8m4 13h8a1 1 0 001-1V9a1 1 0 00-1-1H4a1 1 0 00-1 1v11a1 1 0 001 1h8z" />
                    </svg>
                    <span>Affiliate Applications</span>
                  </Link>
                  <Link
                    href="/growth"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17l6-6 4 4 8-8m0 0h-5m5 0v5" />
                    </svg>
                    <span>Growth</span>
                  </Link>
                  <Link
                    href="/site-tracking"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span>Site Tracking</span>
                  </Link>
                  {/* User Admin: look up a member, cancel their subscription, or delete their account. */}
                  <Link
                    href="/user-admin"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9.75a3 3 0 11-6 0 3 3 0 016 0zM17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0" />
                    </svg>
                    <span>User Admin</span>
                  </Link>
                  {/* Integrations: keys and switches the site reads live, no deploy (owner-integrations-page-1020). */}
                  <Link
                    href="/admin/integrations"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v4m6-4v4M7 7h10v4a5 5 0 01-10 0V7zm5 9v5" />
                    </svg>
                    <span>Integrations</span>
                  </Link>
                  {/* Video Review: watch, approve and download the rendered Shorts (owner, 2026-10-25: "same as site tracking"). */}
                  <Link
                    href="/video-review"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 11h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8zm0 0l1.6-5.6a1 1 0 011.2-.7l12.5 3.4a1 1 0 01.7 1.2L19.4 11M8 6.6l2.5 3.8M12.5 7.8l2.5 3.8" />
                    </svg>
                    <span>Video Review</span>
                  </Link>
                  {/* Music Review: keep or drop the songs the Shorts use; a Drop leaves it out of the render (owner, 2026-10-29). */}
                  <Link
                    href="/music-review"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 18V5l12-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zm12-2a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Music Review</span>
                  </Link>
                  <Link
                    href="/watcher-lab"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                    <span>Watcher Lab</span>
                  </Link>
                  {/* Performance Command — the one console consolidating every strategy we run or track. */}
                  <Link
                    href="/admin/performance"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                    <span>Performance</span>
                  </Link>
                  {/* Revenue Forecast: 12-month subscriber and revenue projection, live from Stripe. */}
                  <Link
                    href="/admin/forecast"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                    <span>Revenue Forecast</span>
                  </Link>
                  {/* What to Watch — alerts ranked by the OOS-validated conviction model (float x not-extended). */}
                  <Link
                    href="/admin/watch"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>What to Watch</span>
                  </Link>
                  {/* Reddit Radar scan list — add / remove the subreddits it monitors. */}
                  <Link
                    href="/admin/subreddits"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-orange-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8a9 9 0 100-18 9 9 0 000 18z" />
                    </svg>
                    <span>Reddit Subreddits</span>
                  </Link>
                  {/* Partner API keys — issue, revoke and monitor customer access (owner: every admin
                      page should be reachable from this menu). */}
                  <Link
                    href="/admin/api-keys"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    <span>API Keys</span>
                  </Link>
                  <Link
                    href="/admin/premium"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.98 9.101c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    <span>Premium</span>
                  </Link>
                  <Link
                    href="/admin/verify-trades"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Verify Trades</span>
                  </Link>
                  <Link
                    href="/admin/post-ideas"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <span>Post Ideas</span>
                  </Link>
                  <Link
                    href="/alpaca"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Alpaca Dashboard</span>
                  </Link>
                  <Link
                    href="/alpacapaper"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3h6m-5 0v5.5L5 18a2 2 0 001.8 3h10.4a2 2 0 001.8-3L14 8.5V3" />
                    </svg>
                    <span>Paper Dashboard</span>
                  </Link>
                  <Link
                    href="/ai-config"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>AI Algo Config</span>
                  </Link>
                  <Link
                    href="/watcher"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-violet-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>Top-Play</span>
                  </Link>
                  <Link
                    href="/weekly-movers?days=1"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span>1-Day Movers</span>
                  </Link>
                  <Link
                    href="/weekly-movers?days=3"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-sky-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span>3-Day Movers</span>
                  </Link>
                  <Link
                    href="/weekly-movers?days=7"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span>7-Day Movers</span>
                  </Link>
                  <Link
                    href="/reddit-posts"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-orange-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    <span>Reddit Posts</span>
                  </Link>
                  <Link
                    href="/reddit-queue"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-orange-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span>Reddit Post Queue</span>
                  </Link>
                  <Link
                    href="/x-queue"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-sky-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4l16 16M20 4L4 20" />
                    </svg>
                    <span>X Post Queue</span>
                  </Link>
                  <Link
                    href="/previousdayalerts"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Yesterday&apos;s Alerts</span>
                  </Link>
                  <Link
                    href="/support-dashboard"
                    onClick={closeDropdown}
                    className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-foreground hover:bg-accent/50 transition-colors group w-full text-left`}
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>Support Dashboard</span>
                  </Link>
                </>
              )}

              {/* Build Version - Only for TheDesperateTrader */}
              {(user.username === 'TheDesperateTrader' || user.name === 'TheDesperateTrader') && buildInfo && (
                <>
                  <div className="border-t border-border/50 my-2"></div>
                  <div className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-2' : 'px-3 py-1.5'} text-xs text-muted-foreground`}>
                    <svg className="w-3 h-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                    <span className="font-mono text-muted-foreground">{buildInfo.shortVersion}</span>
                    <span className="text-muted-foreground">*</span>
                    <span className="text-muted-foreground">
                      {new Date(buildInfo.buildDate).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </>
              )}

              <div className="border-t border-border/50 my-2"></div>

              <button
                onClick={async () => {
                  closeDropdown();
                  await forceSignOut();
                }}
                className={`flex items-center gap-3 ${isDesktop ? 'px-4 py-3' : 'px-3 py-2'} text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors w-full group`}
              >
                <svg className="w-4 h-4 text-red-400 group-hover:text-red-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </header>
    </>
  )
}

// Export with new name to force module graph change
export default NavbarComponent
